/**
 * SkillMatrixGridRow.tsx
 * Memoized row renderer for the Skill Matrix table.
 *
 * Extracted from SkillMatrixGrid to keep that file under the 600-line cap
 * and to avoid re-rendering every row when unrelated cells change selection.
 *
 * Each row is wrapped in React.memo so it only re-renders when its specific
 * user's cells, the selection state, or density changes.
 *
 * C3 (coverage strip): renders a sticky-right "Coverage" cell per row.
 * C4 (collapsible groups): skips columns whose category is in collapsedGroups.
 *
 * Interaction model:
 *   - Plain click  : cycle level (empty → 1 → 2 → 3 → 4 → 5 → empty)
 *   - Shift+click  : extend range selection (handled upstream, row skips)
 *   - Right-click  : open single-cell popover for L1–L5 with descriptive names
 *   - Drag from cell: range selection (onCellMouseDown handles this)
 *   - Hover        : fires onCellHover(coord) so grid can display status strip
 */

import React from 'react';
import { Box, LinearProgress, TableCell, TableRow, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { SkillLevelChip } from '../../../components/schedule/SkillLevelChip';
import type { SkillMatrixCell, MatrixSkill, SkillUser } from '../../../services/scheduleSkillMatrix';
import { FillHandle } from './skill-matrix/FillHandle';
import { cellKey } from './skill-matrix/selectionMath';
import type { CellCoord, SelectionEdges } from './skill-matrix/selectionMath';
import type { Density } from './skill-matrix/density';
import { ROW_HEIGHT_BY_DENSITY, BADGE_SIZE_BY_DENSITY, AVATAR_SIZE_BY_DENSITY } from './skill-matrix/density';
import { EmployeeAvatar } from './skill-matrix/EmployeeAvatar';
import { TierMetaCell } from './skill-matrix/TierMetaCell';
import { computeCoverage } from './skill-matrix/coverage';
import type { MetaColumn } from './skill-matrix/metaColumnCollapse';

/** Width of the sticky Coverage column — must match SkillMatrixGrid.tsx constant. */
const COVERAGE_COL_WIDTH = 120;

/** Width of a collapsed category strip — must match SkillMatrixGrid.tsx constant. */
const COLLAPSED_GROUP_WIDTH = 50;

// Re-export types used by SkillMatrixGrid to build its flatMap.
export interface GroupedSkills {
  category: string;
  items: MatrixSkill[];
}

interface SkillMatrixGridRowProps {
  user: SkillUser;
  rowIdx: number;
  groups: GroupedSkills[];
  /** Set of category names that are currently collapsed. */
  collapsedGroups: Set<string>;
  /** Set of meta-column identifiers (jobClass / workGroup / tier) that are collapsed. */
  collapsedMetaColumns: Set<MetaColumn>;
  /** True when the caller may edit users' Tier (Lab Admin — PUT /users/:key). */
  canEditTier: boolean;
  /** Persist a Tier change for this user (null clears back to auto-derive). */
  onTierChange: (appUserKey: string, tier: string | null) => void;
  cellIndex: Map<string, SkillMatrixCell>;
  /** Full flat cells array (all employees) — used for coverage calculation. */
  cells: SkillMatrixCell[];
  /** Set of SkillKey strings for currently visible (non-collapsed) columns. */
  visibleSkillKeys: Set<string>;
  selectedKeys: Set<string>;
  /** Edge flags for each cell in a multi-cell selection. Empty when 0 or 1 cell selected. */
  selectionEdges: Map<string, SelectionEdges>;
  previewKeys: Set<string>;
  focusCoord: CellCoord | null;
  isDragging: boolean;
  density: Density;
  /** When true the grid is view-only — clicks/drag/right-click never mutate (BUG-2026-0098). */
  readOnly: boolean;
  /** Graph photo blob URLs keyed by email (may be empty while photos load). */
  photos: Record<string, string | null>;
  onCellClick: (cell: SkillMatrixCell, anchorEl: HTMLElement) => void;
  onCellMouseDown: (coord: CellCoord, e: React.MouseEvent) => void;
  onCellMouseEnter: (coord: CellCoord, e: React.MouseEvent) => void;
  onHandleMouseDown: (e: React.MouseEvent) => void;
  /** Called on plain click (no shift) to cycle the cell's level. */
  onCellCycle: (coord: CellCoord) => void;
  /** Called when the pointer enters/leaves a cell — drives the status strip. */
  onCellHover: (coord: CellCoord | null) => void;
}

/** Pick a heatmap bar color token based on coverage percentage. */
function coverageBarColor(
  percent: number,
  schedule: {
    heatmapLow: string;
    heatmapMid: string;
    heatmapHigh: string;
  },
): string {
  if (percent >= 71) return schedule.heatmapLow;   // green
  if (percent >= 31) return schedule.heatmapMid;   // amber
  return schedule.heatmapHigh;                      // red
}

export const SkillMatrixGridRow: React.FC<SkillMatrixGridRowProps> = React.memo(
  ({
    user,
    rowIdx,
    groups,
    collapsedGroups,
    collapsedMetaColumns,
    canEditTier,
    onTierChange,
    cellIndex,
    cells,
    visibleSkillKeys,
    selectedKeys,
    selectionEdges,
    previewKeys,
    focusCoord,
    isDragging,
    density,
    readOnly,
    photos,
    onCellClick,
    onCellMouseDown,
    onCellMouseEnter,
    onHandleMouseDown,
    onCellCycle,
    onCellHover,
  }) => {
    const theme = useTheme();
    const rowBg = rowIdx % 2 === 1 ? theme.table.stripeBg : 'background.paper';
    const rowHeight = ROW_HEIGHT_BY_DENSITY[density];
    const badgeSize = BADGE_SIZE_BY_DENSITY[density];
    const avatarSize = AVATAR_SIZE_BY_DENSITY[density];

    // Compute per-employee coverage for the visible (non-collapsed) columns.
    const coverage = computeCoverage(cells, user.AppUserKey, visibleSkillKeys);
    const barColor = coverageBarColor(coverage.percent, theme.schedule);

    return (
      <TableRow
        sx={{
          bgcolor: rowBg,
          '&:hover': { bgcolor: theme.table.hoverBg },
        }}
      >
        {/* Employee avatar + name — sticky left */}
        <TableCell
          sx={{
            position: 'sticky',
            left: 0,
            bgcolor: rowBg,
            whiteSpace: 'nowrap',
            zIndex: 1,
            minWidth: 200,
            height: rowHeight,
            py: 0,
            px: 1,
          }}
        >
          <EmployeeAvatar
            displayName={user.DisplayName}
            email={user.Email}
            photoUrl={photos[user.Email]}
            jobClass={user.JobClass}
            size={avatarSize}
          />
        </TableCell>

        {/* Job Class — narrow spacer when collapsed */}
        {collapsedMetaColumns.has('jobClass') ? (
          <TableCell
            sx={{
              width: COLLAPSED_GROUP_WIDTH,
              minWidth: COLLAPSED_GROUP_WIDTH,
              maxWidth: COLLAPSED_GROUP_WIDTH,
              height: rowHeight,
              py: 0,
            }}
          />
        ) : (
          <TableCell
            sx={{
              whiteSpace: 'nowrap',
              color: 'text.secondary',
              fontSize: '0.75rem',
              height: rowHeight,
              py: 0,
            }}
          >
            {user.JobClass ?? <em style={{ color: theme.schedule.noActivity }}>Unassigned</em>}
          </TableCell>
        )}

        {/* Work Group — narrow spacer when collapsed */}
        {collapsedMetaColumns.has('workGroup') ? (
          <TableCell
            sx={{
              width: COLLAPSED_GROUP_WIDTH,
              minWidth: COLLAPSED_GROUP_WIDTH,
              maxWidth: COLLAPSED_GROUP_WIDTH,
              height: rowHeight,
              py: 0,
            }}
          />
        ) : (
          <TableCell
            sx={{
              whiteSpace: 'nowrap',
              color: 'text.secondary',
              fontSize: '0.75rem',
              height: rowHeight,
              py: 0,
            }}
          >
            {user.WorkGroup ?? <em style={{ color: theme.schedule.noActivity }}>Unassigned</em>}
          </TableCell>
        )}

        {/* Tier — scheduling seniority group (moved here from Admin → Users, RBAC audit).
            Drives ONLY the Employee Schedule row grouping; admin-editable, auto-derived
            from Job Class when unset. */}
        {collapsedMetaColumns.has('tier') ? (
          <TableCell
            sx={{
              width: COLLAPSED_GROUP_WIDTH,
              minWidth: COLLAPSED_GROUP_WIDTH,
              maxWidth: COLLAPSED_GROUP_WIDTH,
              height: rowHeight,
              py: 0,
            }}
          />
        ) : (
          <TableCell
            sx={{
              whiteSpace: 'nowrap',
              color: 'text.secondary',
              fontSize: '0.75rem',
              height: rowHeight,
              py: 0,
            }}
          >
            <TierMetaCell
              user={user}
              canEdit={canEditTier}
              onTierChange={onTierChange}
            />
          </TableCell>
        )}

        {groups.flatMap(({ category, items }) => {
          // Collapsed group: render a single empty spacer cell to keep column alignment.
          if (collapsedGroups.has(category)) {
            return [
              <TableCell
                key={`${category}__collapsed`}
                sx={{
                  width: COLLAPSED_GROUP_WIDTH,
                  minWidth: COLLAPSED_GROUP_WIDTH,
                  maxWidth: COLLAPSED_GROUP_WIDTH,
                  borderLeft: '2px solid',
                  borderColor: 'divider',
                  height: rowHeight,
                  py: 0,
                  bgcolor: rowBg,
                }}
              />,
            ];
          }

          return items.map((tt) => {
            const key = cellKey(user.AppUserKey, tt.SkillKey);
            const cell = cellIndex.get(key);
            const level = cell?.Level ?? null;
            const isFocused =
              focusCoord?.userKey === user.AppUserKey &&
              focusCoord?.skillKey === tt.SkillKey;
            const isSelected = selectedKeys.has(key);
            const isPreview = previewKeys.has(key);

            const coord: CellCoord = {
              userKey: user.AppUserKey,
              skillKey: tt.SkillKey,
            };

            const syntheticCell: SkillMatrixCell = cell ?? {
              SkillMatrixKey: null,
              UserKey: user.AppUserKey,
              UserDisplayName: user.DisplayName,
              UserEmail: user.Email,
              JobClass: user.JobClass,
              WorkGroup: user.WorkGroup,
              Tier: user.Tier,
              SkillKey: tt.SkillKey,
              SkillName: tt.Name,
              SkillCategory: tt.Category,
              Level: null,
            };

            // ── Selection visuals ────────────────────────────────────────────────
            // Focus is signalled by the SkillLevelChip's own dashed ring.
            // Multi-cell selection (selectionEdges defined): strong 20% sage fill +
            //   solid inset box-shadow perimeter around the whole rectangle.
            // Single-cell selected (isSelected but no edges entry): subtle 6% tint only.
            // Preview (drag-fill): 4% tint + dashed outline — unchanged.
            const edges = selectionEdges.get(key);
            const isMultiSelected = edges !== undefined;

            const cellBg = isMultiSelected
              ? alpha(theme.palette.primary.main, 0.20)
              : isSelected
              ? alpha(theme.palette.primary.main, 0.06)
              : isPreview
              ? alpha(theme.palette.primary.main, 0.04)
              : undefined;

            // Build directional inset shadows that form a continuous perimeter box
            // across adjacent cells. 2px, no layout shift.
            const shadowParts: string[] = [];
            if (edges?.top)    shadowParts.push(`inset 0 2px 0 0 ${theme.palette.primary.main}`);
            if (edges?.bottom) shadowParts.push(`inset 0 -2px 0 0 ${theme.palette.primary.main}`);
            if (edges?.left)   shadowParts.push(`inset 2px 0 0 0 ${theme.palette.primary.main}`);
            if (edges?.right)  shadowParts.push(`inset -2px 0 0 0 ${theme.palette.primary.main}`);
            const boxShadow = shadowParts.length > 0 ? shadowParts.join(', ') : undefined;

            return (
              <TableCell
                key={tt.SkillKey}
                align="center"
                data-user-key={user.AppUserKey}
                data-task-type-key={tt.SkillKey}
                // View-only: mousedown (range-select), click (cycle) and right-click
                // (popover) are all inert so a stray click never mutates data.
                // Hover stays wired so the status strip still reads the cell.
                onMouseDown={readOnly ? undefined : (e) => onCellMouseDown(coord, e)}
                onMouseEnter={(e) => {
                  if (!readOnly) onCellMouseEnter(coord, e);
                  onCellHover(coord);
                }}
                onMouseLeave={() => onCellHover(null)}
                onClick={readOnly ? undefined : (e) => {
                  // Shift+click is handled by the range-selection hook upstream.
                  if (e.shiftKey) return;
                  onCellCycle(coord);
                }}
                onContextMenu={readOnly ? undefined : (e) => {
                  e.preventDefault();
                  // Right-click opens the popover with full L1–L5 descriptive labels.
                  onCellClick(syntheticCell, e.currentTarget);
                }}
                sx={{
                  position: 'relative',
                  cursor: readOnly ? 'default' : 'pointer',
                  p: 0.5,
                  userSelect: 'none',
                  height: rowHeight,
                  bgcolor: cellBg,
                  boxShadow,
                  // Dashed outline for drag-preview (shows which cells will be filled).
                  // No inset ring on single-cell focus — focus lives in the chip ring.
                  outline: isPreview
                    ? `1px dashed ${theme.palette.primary.main}`
                    : undefined,
                  outlineOffset: isPreview ? '-4px' : undefined,
                  transition: 'background-color 0.1s ease, box-shadow 0.1s ease',
                  // Reveal the fill handle on hover (Excel/Sheets pattern).
                  // The handle hides itself (opacity:0) and this un-hides it.
                  '&:hover .skill-fill-handle': { opacity: 1 },
                }}
              >
                {/* showEmpty + focused: empty ring turns sage when this cell has keyboard focus */}
                <SkillLevelChip level={level} pixelSize={badgeSize} showEmpty focused={isFocused} />
                {!readOnly && isFocused && level != null && (
                  <FillHandle onMouseDown={onHandleMouseDown} />
                )}
              </TableCell>
            );
          });
        })}

        {/* Coverage column — sticky right */}
        <TableCell
          align="center"
          sx={{
            position: 'sticky',
            right: 0,
            bgcolor: rowBg,
            borderLeft: '2px solid',
            borderColor: 'divider',
            width: COVERAGE_COL_WIDTH,
            minWidth: COVERAGE_COL_WIDTH,
            height: rowHeight,
            py: 0,
            px: 1,
            zIndex: 1,
          }}
        >
          {coverage.denominator === 0 ? (
            <Typography
              variant="caption"
              sx={{ color: 'text.disabled', fontStyle: 'italic' }}
            >
              —
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, px: 0.5 }}>
              <LinearProgress
                variant="determinate"
                value={coverage.percent}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'action.disabledBackground',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: barColor,
                    borderRadius: 3,
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  color: barColor,
                  lineHeight: 1,
                  textAlign: 'center',
                }}
              >
                {coverage.percent}%
              </Typography>
            </Box>
          )}
        </TableCell>
      </TableRow>
    );
  },
);

SkillMatrixGridRow.displayName = 'SkillMatrixGridRow';
